/**
 * Token Counter & Prompt Size Estimator utility functions
 * Complete implementation with js-tiktoken for token counting
 */

import { getEncoding } from 'js-tiktoken'

export interface ContextPresetConfig {
  name: string
  encoding: string
  contextLimit: number
  description: string
}

export const CONTEXT_ESTIMATE_DISCLAIMER =
  'Token counts and context percentages are planning estimates. Provider-native tokenizers and context tiers can differ, so verify critical limits with the model provider before production use.'

// Generic context-window presets avoid presenting this tool as a live model catalog.
export const CONTEXT_PRESETS: Record<string, ContextPresetConfig> = {
  'context-32k': {
    name: '32K context window',
    encoding: 'cl100k_base',
    contextLimit: 32000,
    description: 'Small-to-medium long-context workflows'
  },
  'context-128k': {
    name: '128K context window',
    encoding: 'cl100k_base',
    contextLimit: 128000,
    description: 'Common long-context planning reference'
  },
  'context-200k': {
    name: '200K context window',
    encoding: 'cl100k_base',
    contextLimit: 200000,
    description: 'Large coding sessions and long documents'
  },
  'context-1m': {
    name: '1M context window',
    encoding: 'cl100k_base',
    contextLimit: 1048576,
    description: 'Very large-context planning reference'
  }
}

export interface TokenStats {
  text: string
  characterCount: number
  wordCount: number
  tokenCount: number
  sentenceCount: number
}

export interface ContextFitEstimate {
  presetId: string
  presetName: string
  description: string
  inputTokens: number
  outputTokens: number
  totalTokens: number
  contextLimit: number
  percentageOfLimit: number
  withinLimit: boolean
}

/**
 * Count tokens in text using specified encoding
 */
export function countTokens(text: string, encodingName: string = 'cl100k_base'): number {
  try {
    const encoding = getEncoding(encodingName as Parameters<typeof getEncoding>[0])
    const tokens = encoding.encode(text)
    return tokens.length
  } catch (error) {
    console.error('Error counting tokens:', error)
    // Fallback: estimate tokens as ~1 token per 4 characters
    return Math.ceil(text.length / 4)
  }
}

/**
 * Get detailed statistics for text
 */
export function getTextStats(text: string, encodingName: string = 'cl100k_base'): TokenStats {
  const characterCount = text.length
  const wordCount = text.trim() ? text.trim().split(/\s+/).length : 0
  const tokenCount = countTokens(text, encodingName)

  // Count sentences (simple approach)
  const sentenceCount = text.split(/[.!?]+/).filter(s => s.trim().length > 0).length

  return {
    text,
    characterCount,
    wordCount,
    tokenCount,
    sentenceCount
  }
}

/**
 * Calculate context fit for a specific context-window preset
 */
export function calculateContextFit(
  inputText: string,
  outputText: string,
  presetId: string
): ContextFitEstimate {
  const preset = CONTEXT_PRESETS[presetId]

  if (!preset) {
    throw new Error(`Context preset ${presetId} not found`)
  }

  const inputTokens = countTokens(inputText, preset.encoding)
  const outputTokens = countTokens(outputText, preset.encoding)

  return calculateContextFitFromTokenCounts(inputTokens, outputTokens, presetId)
}

/**
 * Calculate context fit from already-estimated token counts
 */
export function calculateContextFitFromTokenCounts(
  inputTokens: number,
  outputTokens: number,
  presetId: string
): ContextFitEstimate {
  const preset = CONTEXT_PRESETS[presetId]

  if (!preset) {
    throw new Error(`Context preset ${presetId} not found`)
  }

  const totalTokens = inputTokens + outputTokens
  const percentageOfLimit = (totalTokens / preset.contextLimit) * 100
  const withinLimit = totalTokens <= preset.contextLimit

  return {
    presetId,
    presetName: preset.name,
    description: preset.description,
    inputTokens,
    outputTokens,
    totalTokens,
    contextLimit: preset.contextLimit,
    percentageOfLimit,
    withinLimit
  }
}

/**
 * Calculate context fit for all presets
 */
export function calculateAllContextFits(
  inputText: string,
  outputText: string
): ContextFitEstimate[] {
  return Object.keys(CONTEXT_PRESETS).map(presetId =>
    calculateContextFit(inputText, outputText, presetId)
  )
}

/**
 * Calculate context fit for all presets with output estimated as a token ratio
 */
export function calculateAllContextFitsFromEstimatedOutput(
  inputText: string,
  outputRatio: number
): ContextFitEstimate[] {
  return Object.entries(CONTEXT_PRESETS).map(([presetId, preset]) => {
    const inputTokens = countTokens(inputText, preset.encoding)
    const outputTokens = estimateOutputTokens(inputTokens, outputRatio)

    return calculateContextFitFromTokenCounts(inputTokens, outputTokens, presetId)
  })
}

/**
 * Format large numbers with commas
 */
export function formatNumber(num: number): string {
  return num.toLocaleString('en-US')
}

/**
 * Estimate output tokens based on input tokens (rough heuristic)
 */
export function estimateOutputTokens(
  inputTokens: number,
  outputRatio: number = 0.5
): number {
  return Math.round(inputTokens * outputRatio)
}

/**
 * Check if text exceeds context limit
 */
export function checkContextLimit(
  text: string,
  presetId: string
): { withinLimit: boolean; tokens: number; limit: number; percentage: number } {
  const preset = CONTEXT_PRESETS[presetId]
  if (!preset) {
    throw new Error(`Context preset ${presetId} not found`)
  }

  const tokens = countTokens(text, preset.encoding)
  const percentage = (tokens / preset.contextLimit) * 100

  return {
    withinLimit: tokens <= preset.contextLimit,
    tokens,
    limit: preset.contextLimit,
    percentage
  }
}

/**
 * Split text into chunks that fit within context limit
 */
export function splitTextByTokenLimit(
  text: string,
  presetId: string,
  overlapTokens: number = 100
): string[] {
  const preset = CONTEXT_PRESETS[presetId]
  if (!preset) {
    throw new Error(`Context preset ${presetId} not found`)
  }

  const encoding = getEncoding(preset.encoding as Parameters<typeof getEncoding>[0])
  const tokens = encoding.encode(text)

  const chunkSize = preset.contextLimit - overlapTokens
  const chunks: string[] = []

  for (let i = 0; i < tokens.length; i += chunkSize) {
    const chunkTokens = tokens.slice(i, i + chunkSize + overlapTokens)
    const decoded = encoding.decode(chunkTokens)
    const chunkText = Array.isArray(decoded) ? String.fromCharCode(...decoded) : decoded
    chunks.push(chunkText)
  }

  return chunks
}
